/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 패널 클래스를 정의한다.
 *
 * @file /src/components/panel/Panel.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 18.
 */
import * as parent from '@base/Component';
import * as title from '@elements/title/Bar';
import { IndicatorType, Loading } from '@elements/loading/Loading';
import { Task } from '@components/taskbar/elements/Task';
import { Toolbar, Properties as ToolbarProperties } from '@components/toolbar/Toolbar';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Resizer } from '@utils/Resizer';
import { Utils } from '@utils/Utils';
import { Language, LanguageText } from '@utils/Language';
import './Panel.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 컴포넌트가 보여질 떄
     */
    show?: (me: T) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 패널 아이콘
     */
    icon?: string;

    /**
     * 패널 제목 (텍스트 또는 Title 엘리먼트)
     */
    title?: title.Properties<title.Bar> | title.Properties<title.Bar>['text'];

    /**
     * 상단바
     */
    topbar?: Toolbar | ToolbarProperties<Toolbar>['tools'];

    /**
     * 다중상단바 (다중상단바가 정의된 경우 상단바 설정은 무시된다.)
     */
    topbars?: (Toolbar | ToolbarProperties<Toolbar>['tools'])[];

    /**
     * 하단바
     */
    bottombar?: Toolbar | ToolbarProperties<Toolbar>['tools'];

    /**
     * 하단바 (다중하단바가 정의된 경우 하단바 설정은 무시된다.)
     */
    bottombars?: (Toolbar | ToolbarProperties<Toolbar>['tools'])[];

    /**
     * 닫기가능여부 (다른 컴포넌트에서 사용된다.)
     */
    closable?: boolean;

    /**
     * 이동가능여부 (다른 컴포넌트에서 사용된다.)
     */
    movable?: boolean;

    /**
     * 크기조절여부
     */
    resizable?: boolean | [boolean, boolean, boolean, boolean];

    /**
     * 크기조절 원복을 위한 storage key 값
     */
    resizeNm?: string;

    /**
     * 로딩메시지 타입
     */
    loadingType?: IndicatorType;

    /**
     * 로딩메시지
     */
    loadingText?: LanguageText;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Panel extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'panel';

    /**
     * 패널과 연결된 작업표시줄의 작업
     */
    task: Task = null;

    /**
     * 패널 제목
     */
    title: title.Bar | null;

    /**
     * 상단바
     */
    topbars: Toolbar[] = [];

    /**
     * 하단바
     */
    bottombars: Toolbar[] = [];

    /**
     * 닫기가능여부 (다른 컴포넌트에서 사용된다.)
     */
    closable: boolean = false;

    /**
     * 이동가능여부 (다른 컴포넌트에서 사용된다.)
     */
    movable: boolean = false;

    /**
     * 크기조절여부
     */
    resizable: boolean | [boolean, boolean, boolean, boolean] = false;

    /**
     * 크기조절 원복을 위한 storage key 값
     */
    resizeNm: string = null;

    /**
     * Resizer
     */
    resizer: Resizer = null;

    /**
     * 로딩
     */
    loading: Loading = null;

    /**
     * 헤더 DOM
     */
    $header: Dom = null;

    /**
     * 푸터 DOM
     */
    $footer: Dom = null;

    /**
     * 패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Panel> = null) {
        super(properties);

        this.title = this.createTitle(this.properties?.title, this.properties?.icon ?? null);
        this.topbars = this.createToolbar(this.properties?.topbars ?? this.properties?.topbar, 'top');
        this.bottombars = this.createToolbar(this.properties?.bottombars ?? this.properties?.bottombar, 'bottom');
        this.closable = this.properties?.closable === true;
        this.movable = this.properties?.movable === true;
        this.resizable = this.properties?.resizable ?? false;
        this.resizeNm = this.properties?.resizeNm ?? null;
        this.scrollable = this.properties?.scrollable ?? false;
        this.$scrollable = this.$getContent();
    }

    /**
     * 작업표시줄과 연결된 경우 작업을 지정한다.
     *
     * @param task - 작업
     */
    setTask(task: Task): void {
        this.task = task;
    }

    /**
     * 작업표시줄과 연결된 작업이 있을 경우 가져온다.
     *
     * @returns 작업
     */
    getTask(): Task | null {
        if (this.task !== null) {
            return this.task;
        }

        let parent: parent.Component = this;
        while (parent !== null) {
            if (parent instanceof Panel && parent.task !== null) {
                this.task = parent.task;
                return parent.task;
            }

            parent = parent.getParent();
        }

        return null;
    }

    /**
     * 헤더 DOM 을 가져온다.
     *
     * @returns 헤더 DOM
     */
    $getHeader(): Dom {
        if (this.$header === null) {
            this.$header = Html.create('div');
            this.$header.setAttr('data-role', 'header');
        }

        return this.$header;
    }

    /**
     * 푸터 DOM 을 가져온다.
     *
     * @returns 푸터 DOM
     */
    $getFooter(): Dom {
        if (this.$footer === null) {
            this.$footer = Html.create('div');
            this.$footer.setAttr('data-role', 'footer');
        }

        return this.$footer;
    }

    /**
     * 제목 엘리먼트를 생성한다.
     *
     * @param properties - 제목설정
     * @returns 제목 엘리먼트
     */
    protected createTitle(
        properties: title.Properties<title.Bar> | title.Properties<title.Bar>['text'] | undefined,
        icon: string | null = null
    ): title.Bar | null {
        if (properties === undefined) {
            return null;
        }

        let element: title.Bar | null = null;
        if (typeof properties === 'object' && 'text' in properties) {
            element = new title.Bar(properties);
        } else {
            element = new title.Bar({ text: properties });
        }
        if (icon !== null) {
            element.setIcon(icon);
        }

        element?.setComponent(this);

        return element;
    }

    /**
     * 툴바 엘리먼트를 생성한다.
     *
     * @param toolbar - 툴바설정
     * @returns 툴바 엘리먼트
     */
    protected createToolbar(
        properties:
            | Toolbar
            | ToolbarProperties<Toolbar>['tools']
            | (Toolbar | ToolbarProperties<Toolbar>['tools'])[]
            | undefined,
        position: 'top' | 'bottom'
    ): Toolbar[] {
        if (properties === undefined || properties === null) {
            return [];
        }

        let items: (Toolbar | ToolbarProperties<Toolbar>['tools'])[];
        const toolbars: Toolbar[] = [];

        if (
            Array.isArray(properties) == true &&
            properties.length > 0 &&
            (properties[0] instanceof Toolbar || Array.isArray(properties[0]) == true)
        ) {
            items = properties as (Toolbar | ToolbarProperties<Toolbar>['tools'])[];
        } else {
            items = [properties as Toolbar | ToolbarProperties<Toolbar>['tools']];
        }

        for (const item of items) {
            let toolbar: Toolbar;

            if (item instanceof Toolbar) {
                toolbar = item;
            } else {
                toolbar = new Toolbar({ tools: item as ToolbarProperties<Toolbar>['tools'] });
            }

            toolbar.setParent(this);
            toolbar.setPosition(position);

            toolbars.push(toolbar);
        }

        return toolbars;
    }

    /**
     * 타이틀을 설정한다.
     *
     * @param title - 타이틀
     * @param icon - 아이콘
     */
    setTitle(
        properties: title.Properties<title.Bar> | title.Properties<title.Bar>['text'] | undefined,
        icon: string | null = null
    ): title.Bar {
        this.title?.remove();

        this.title = this.createTitle(properties, icon);
        if (this.isRendered() == true) {
            this.$getHeader().append(this.title.$getElement(), 0);
            this.title.render();
        }

        return this.title;
    }

    /**
     * 타이틀을 가져온다.
     *
     * @returns 타이틀
     */
    getTitle(): title.Bar | null {
        return this.title;
    }

    /**
     * 패널의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 패널
     */
    setDisabled(disabled: boolean): this {
        this.$getComponent().toggleAttr('disabled', disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 로딩메시지를 세팅한다.
     *
     * @param loadingType - 로딩메시지 타입 또는 로딩메시지를 보일지 여부
     * @param loadingText - 로딩메시지
     * @returns 로딩메시지
     */
    setLoading(loadingType: IndicatorType | boolean | null = null, loadingText: LanguageText = null): void {
        const isLoading = loadingType !== false;
        const indicator = typeof loadingType == 'boolean' ? null : loadingType;

        if (isLoading === false) {
            this.loading?.hide();
            return;
        }

        if (this.loading === null) {
            this.loading = new Loading({
                indicator: indicator ?? this.properties?.loadingType ?? 'column',
                direction: 'column',
                text: loadingText ?? this.properties?.loadingText ?? null,
            });

            this.loading.setComponent(this);
            this.$getContainer().append(this.loading.$getElement());
        } else {
            this.loading.setIndicator(indicator ?? this.properties?.loadingType ?? 'column');
            this.loading.setText(loadingText ?? this.properties?.loadingText ?? null);
        }

        this.loading.show();
    }

    /**
     * 패널이 로딩상태인지 확인한다.
     *
     * @returns 로딩여부
     */
    isLoading(): boolean {
        return this.loading?.isShow() === true;
    }

    /**
     * 컴포넌트 최대너비(px)를 설정한다.
     *
     * @param maxWidth - 최대너비
     */
    setMaxWidth(maxWidth: number): void {
        super.setMaxWidth(maxWidth);
        this.resizer?.setMaxWidth(maxWidth);
    }

    /**
     * 컴포넌트 최대높이(px)를 설정한다.
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: number): void {
        super.setMaxHeight(maxHeight);
        this.resizer?.setMaxHeight(maxHeight);
    }

    /**
     * 컴포넌트 최소너비를 설정한다.
     *
     * @param minWidth - 최소너비
     */
    setMinWidth(minWidth: number): void {
        super.setMinWidth(minWidth);
        this.resizer?.setMinWidth(minWidth);
    }

    /**
     * 컴포넌트 최소높이를 설정한다.
     *
     * @param minHeight - 최소높이
     */
    setMinHeight(minHeight: number): void {
        super.setMinHeight(minHeight);
        this.resizer?.setMinHeight(minHeight);
    }

    /**
     * 헤더를 렌더링한다.
     */
    protected async renderHeader(): Promise<void> {
        if (this.title !== null) {
            this.$getHeader().append(this.title.$getElement());
            await this.title.render();
        }

        for (const toolbar of this.topbars) {
            this.$getHeader().append(toolbar.$getComponent());
            toolbar.setParent(this);
            await toolbar.render(true);
        }
    }

    /**
     * 푸터를 렌더링한다.
     */
    protected async renderFooter(): Promise<void> {
        for (const bottombar of this.bottombars) {
            this.$getFooter().append(bottombar.$getComponent());
            bottombar.setParent(this);
            await bottombar.render(true);
        }
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        if (this.disabled == true) {
            this.$getComponent().setAttr('disabled', 'disabled');
        } else {
            if (this.layout != 'row' && this.layout != 'column') {
                this.setLoading('column', Language.getAction('RENDERING'));
            }
        }

        await super.renderDom();
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderContainer(is_force: boolean = false): Promise<void> {
        this.$getContainer().append(this.$getHeader());
        this.$getContainer().append(this.$getContent());
        this.$getContainer().append(this.$getFooter());

        await this.renderHeader();
        await this.renderFooter();
        await this.renderItems(is_force);
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();

        this.$getComponent().toggleAttr('disabled', this.disabled);

        if (this.resizable == false || this.resizer !== null) {
            this.setLoading(false);
            return;
        }

        const directions: [boolean, boolean, boolean, boolean] =
            this.resizable === true ? [true, true, true, true] : this.resizable;

        if (this.resizeNm !== null) {
            const storeResize = Utils.session(`${this.type}_${this.variant}_${this.resizeNm}`) ?? null;

            if (storeResize !== null) {
                if (directions[1] == true || directions[3] == true) {
                    this.setWidth(storeResize.width);
                }

                if (directions[0] == true || directions[2] == true) {
                    this.setHeight(storeResize.height);
                }
            }
        }

        this.resizer = new Resizer(this.$component, this.$component.getParent(), {
            directions: directions,
            guidelines: directions,
            minWidth: (typeof this.minWidth === 'string' ? parseFloat(this.minWidth) : this.minWidth) ?? 0,
            maxWidth: (typeof this.maxWidth === 'string' ? parseFloat(this.maxWidth) : this.maxWidth) ?? 0,
            listeners: {
                end: (_, rect: DOMRect) => {
                    const { left, top, width, height } = rect;
                    if (this.variant === 'window') {
                        this.$getComponent()
                            .setStyle('transform', '')
                            .setStyle('left', `${left}px`)
                            .setStyle('top', `${top}px`)
                            .setStyle('right', 'unset')
                            .setStyle('bottom', 'unset');
                    }

                    if (directions[1] || directions[3]) this.setWidth(width);
                    if (directions[0] || directions[2]) this.setHeight(height);

                    if (this.resizeNm !== null) {
                        Utils.session(`${this.type}_${this.variant}_${this.resizeNm}`, {
                            width: width,
                            height: height,
                        });
                    }

                    this.onResize(width, height);
                },
            },
        });

        this.setLoading(false);
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await this.waitForEvents();

        this.title?.remove(false);
        this.title = null;

        for (const topbbar of this.topbars) {
            topbbar.remove(false);
        }
        this.topbars = [];

        for (const bottombar of this.bottombars) {
            bottombar.remove(false);
        }
        this.bottombars = [];

        this.$header = null;
        this.$footer = null;

        await super.remove(removeDom);
    }

    /**
     * 리사이저에 의해 리사이즈되었을 때
     *
     * @param width - 변경된 가로크기
     * @param height - 변경된 세로크기
     */
    async onResize(width: number, height: number): Promise<void> {
        await this.fireEvent('resize', [this, width, height]);
    }
}
